import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { 
  Clock, Plus, Search, Check, X, 
  AlertCircle, CheckCircle, XCircle, Timer, MapPin 
} from 'lucide-react';
import { timeManagementAPI, hcmAPI } from '../../../services/api';
import { toast } from 'sonner';

const Attendances = () => {
  const navigate = useNavigate();
  const [attendances, setAttendances] = useState([]);
  const [attendanceTypes, setAttendanceTypes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [attendancesRes, typesRes, employeesRes] = await Promise.all([
        timeManagementAPI.getAttendances(),
        timeManagementAPI.getAttendanceTypes(),
        hcmAPI.getEmployees()
      ]);
      setAttendances(attendancesRes.data || []);
      setAttendanceTypes(typesRes.data || []);
      setEmployees(employeesRes.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load attendances');
    } finally {
      setLoading(false);
    }
  };

  const filteredAttendances = attendances.filter(attendance => {
    const matchesSearch = 
      (attendance.employee_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (attendance.employee_id || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || attendance.approval_status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleApprove = async (attendanceId) => {
    try {
      await timeManagementAPI.updateAttendance(attendanceId, { 
        approval_status: 'Approved',
        approved_by: 'ADMIN'
      });
      toast.success('Attendance approved');
      loadData();
    } catch (error) {
      toast.error('Failed to approve attendance');
    }
  };

  const handleReject = async (attendanceId) => {
    try {
      await timeManagementAPI.updateAttendance(attendanceId, { 
        approval_status: 'Rejected',
        approved_by: 'ADMIN'
      });
      toast.success('Attendance rejected');
      loadData();
    } catch (error) {
      toast.error('Failed to reject attendance');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      'Pending': { color: 'bg-yellow-100 text-yellow-700', icon: AlertCircle },
      'Approved': { color: 'bg-green-100 text-green-700', icon: CheckCircle },
      'Rejected': { color: 'bg-red-100 text-red-700', icon: XCircle },
      'Cancelled': { color: 'bg-gray-100 text-gray-700', icon: XCircle },
    };
    const { color, icon: Icon } = config[status] || config['Pending'];
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${color}`}>
        <Icon className="h-3 w-3" />
        {status}
      </span>
    );
  };

  // Calculate total hours
  const totalApprovedHours = attendances
    .filter(a => a.approval_status === 'Approved')
    .reduce((sum, a) => sum + (a.attendance_hours || 0), 0);

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="attendances-page">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <span className="cursor-pointer hover:text-gray-700" onClick={() => navigate('/hcm')}>HCM</span>
            <span>/</span>
            <span className="cursor-pointer hover:text-gray-700" onClick={() => navigate('/hcm/time')}>Time Management</span>
            <span>/</span>
            <span>Attendances</span>
          </div>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Attendance Records</h1>
              <p className="text-gray-600 mt-1">Track overtime, business trips, and special attendances (IT2002)</p>
            </div>
            <Button onClick={() => setShowForm(true)} data-testid="btn-create-attendance">
              <Plus className="h-4 w-4 mr-2" />
              Record Attendance
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <AlertCircle className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Pending</p>
                <p className="text-xl font-semibold text-yellow-600">
                  {attendances.filter(a => a.approval_status === 'Pending').length}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Approved</p>
                <p className="text-xl font-semibold text-green-600">
                  {attendances.filter(a => a.approval_status === 'Approved').length}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Timer className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Approved Hours</p>
                <p className="text-xl font-semibold text-orange-600">{totalApprovedHours.toFixed(1)}h</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Clock className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Records</p>
                <p className="text-xl font-semibold">{attendances.length}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input 
              placeholder="Search by employee..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">All Statuses</option>
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
          </select>
        </div>

        {/* Table */}
        <Card className="overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : filteredAttendances.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No attendance records found.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Employee</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Type</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Date</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Time</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Hours</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Location</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredAttendances.map((attendance) => (
                    <tr key={attendance.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-gray-900">{attendance.employee_name || attendance.employee_id}</p>
                          <p className="text-sm text-gray-500">{attendance.employee_id}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {attendance.attendance_type_name || attendance.attendance_type_id}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {attendance.attendance_date}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4 text-gray-400" />
                          {attendance.start_time} - {attendance.end_time}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-700">
                        {attendance.attendance_hours}h
                        {attendance.net_hours && attendance.net_hours !== attendance.attendance_hours && (
                          <span className="text-gray-500 text-xs ml-1">(net: {attendance.net_hours}h)</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {attendance.location ? (
                          <div className="flex items-center gap-1">
                            <MapPin className="h-4 w-4 text-gray-400" />
                            {attendance.location}
                          </div>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(attendance.approval_status)}
                      </td>
                      <td className="px-4 py-3">
                        {attendance.approval_status === 'Pending' && (
                          <div className="flex items-center gap-2">
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-green-600 hover:bg-green-50"
                              onClick={() => handleApprove(attendance.id)}
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => handleReject(attendance.id)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Create Form Modal */}
        {showForm && (
          <AttendanceFormModal 
            attendanceTypes={attendanceTypes}
            employees={employees}
            onClose={() => setShowForm(false)}
            onSave={() => { setShowForm(false); loadData(); }}
          />
        )}
      </div>
    </Layout>
  );
};

// Attendance Form Modal
const AttendanceFormModal = ({ attendanceTypes, employees, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    employee_id: '',
    attendance_type_id: '',
    attendance_date: '',
    start_time: '09:00',
    end_time: '18:00',
    attendance_hours: 8,
    break_hours: 0,
    location: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [workingDayWarning, setWorkingDayWarning] = useState(null);

  // Auto-calculate hours when times change
  useEffect(() => {
    if (formData.start_time && formData.end_time) {
      const start = new Date(`2000-01-01T${formData.start_time}`);
      const end = new Date(`2000-01-01T${formData.end_time}`);
      const hours = (end - start) / (1000 * 60 * 60);
      if (hours > 0) {
        setFormData(prev => ({
          ...prev,
          attendance_hours: hours,
          net_hours: hours - (prev.break_hours || 0)
        }));
      }
    }
  }, [formData.start_time, formData.end_time]);

  // Check if date is a working day
  useEffect(() => {
    const checkDate = async () => {
      if (formData.employee_id && formData.attendance_date) {
        try {
          const response = await timeManagementAPI.checkWorkingDay({
            employee_id: formData.employee_id,
            check_date: formData.attendance_date
          });
          if (!response.data.is_working_day) {
            setWorkingDayWarning({
              date: formData.attendance_date,
              day: response.data.day_name,
              reason: response.data.non_working_reason
            });
          } else {
            setWorkingDayWarning(null);
          }
        } catch (error) {
          console.error('Failed to check working day:', error);
          setWorkingDayWarning(null);
        }
      } else {
        setWorkingDayWarning(null);
      }
    };

    const debounceTimer = setTimeout(checkDate, 300);
    return () => clearTimeout(debounceTimer);
  }, [formData.employee_id, formData.attendance_date]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.employee_id || !formData.attendance_type_id || !formData.attendance_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await timeManagementAPI.createAttendance(formData);
      toast.success('Attendance recorded successfully');
      onSave();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const firstError = detail[0];
        const fieldName = firstError?.loc?.slice(-1)[0] || 'field';
        toast.error(`${fieldName}: ${firstError?.msg || 'Validation error'}`);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Failed to create attendance');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <div className="p-6 border-b">
          <h2 className="text-lg font-semibold">Record Attendance</h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Employee *</label>
            <select
              value={formData.employee_id}
              onChange={(e) => setFormData({...formData, employee_id: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              required
            >
              <option value="">Select Employee</option>
              {employees.map(emp => (
                <option key={emp.employee_id} value={emp.employee_id}>
                  {emp.personal_data?.first_name} {emp.personal_data?.last_name} ({emp.employee_id})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Attendance Type *</label>
            <select
              value={formData.attendance_type_id}
              onChange={(e) => setFormData({...formData, attendance_type_id: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              required
            >
              <option value="">Select Type</option>
              {attendanceTypes.filter(t => t.status === 'Active').map(type => (
                <option key={type.attendance_type_id} value={type.attendance_type_id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
            <Input 
              type="date"
              value={formData.attendance_date}
              onChange={(e) => setFormData({...formData, attendance_date: e.target.value})}
              required
            />
          </div>
          
          {/* Warning for Non-Working Day */}
          {workingDayWarning && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-amber-800 text-sm">Warning: This is a non-working day</p>
                  <p className="text-xs text-amber-700 mt-1">
                    {workingDayWarning.reason}
                  </p>
                  <p className="text-xs text-amber-600 mt-1 italic">
                    Consider changing the date to a working day.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Time *</label>
              <Input 
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData({...formData, start_time: e.target.value})}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Time *</label>
              <Input 
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData({...formData, end_time: e.target.value})}
                required
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Total Hours</label>
              <Input 
                type="number"
                value={formData.attendance_hours}
                onChange={(e) => setFormData({...formData, attendance_hours: parseFloat(e.target.value)})}
                step="0.5"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Break Hours</label>
              <Input 
                type="number"
                value={formData.break_hours}
                onChange={(e) => setFormData({...formData, break_hours: parseFloat(e.target.value)})}
                step="0.25"
                min="0"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <Input 
              value={formData.location}
              onChange={(e) => setFormData({...formData, location: e.target.value})}
              placeholder="Work location"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              rows={2}
              placeholder="Additional notes"
            />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Attendances;
